import type { SxProps, Theme } from "@mui/material";
import { type FC, type ReactNode, useMemo } from "react";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../../components/Dashboard/CommonDashboardHeader";
import { InHouseLineChart } from "../../../components/UIToolkit/Graphs/LineChart/InHouseLineChart";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { CHART_REFERENCE, LABOR_MIX } from "../../../theme/tokens";
import type { ManagementCenterStaffMixResponse } from "../../../utils/models";
import { type ClampRange, fetchedDataToChartDataGroup, getCensusYDomain } from "../utils";

type Props = {
	data?: ManagementCenterStaffMixResponse;
	isLoading: boolean;
	isActual?: boolean;
	trailing?: ReactNode;
	containerSx?: SxProps<Theme>;
	/** Selected range; clamps edge weekly buckets so the chart never plots a point outside the selection. */
	clampRange?: ClampRange;
};

export const StaffMix: FC<Props> = ({ data, isLoading, isActual, trailing, containerSx, clampRange }) => {
	const { timezone } = useHospitalData();

	const transformedDataLeft =
		!isLoading && data
			? [
					fetchedDataToChartDataGroup({
						data: data.dailyOtPercent,
						name: "DAILY OT(%)",
						timezone,
						clampRange,
						color: LABOR_MIX.DAILY_OT,
					}),
					fetchedDataToChartDataGroup({
						data: data.weeklyOtPercent,
						name: "WEEKLY OT(%)",
						timezone,
						clampRange,
						color: LABOR_MIX.WEEKLY_OT,
					}),
					fetchedDataToChartDataGroup({
						data: data.agencyPercent,
						name: "AGENCY(%)",
						timezone,
						clampRange,
						color: LABOR_MIX.AGENCY,
					}),
				]
			: undefined;

	const transformedDataRight =
		!isLoading && data && transformedDataLeft
			? [
					fetchedDataToChartDataGroup({
						data: data.census,
						name: "CENSUS",
						timezone,
						clampRange,
						color: CHART_REFERENCE.CENSUS,
					}),
				]
			: undefined;

	const censusDomain = getCensusYDomain(data?.census);

	const stackedMax = useMemo(() => {
		// Compute stacked max (sum of all series per date) with 20% headroom
		if (!data) return undefined;
		const allDates = new Set([...Object.keys(data.dailyOtPercent), ...Object.keys(data.weeklyOtPercent), ...Object.keys(data.agencyPercent)]);
		let max = 0;
		for (const date of allDates) {
			const sum = (data.dailyOtPercent[date] ?? 0) + (data.weeklyOtPercent[date] ?? 0) + (data.agencyPercent[date] ?? 0);
			if (sum > max) max = sum;
		}
		return Math.ceil(max * 1.2);
	}, [data]);

	return (
		<CommonDashboardBox
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "start",
				gap: 1,
				width: "100%",
				...containerSx,
			}}
		>
			<CommonDashboardHeader
				label={"Staff mix"}
				subLabel={isActual != null ? `(${isActual ? "Actual" : "Scheduled"})` : undefined}
				gap={1}
				trailing={trailing}
			/>
			<InHouseLineChart
				width={"100%"}
				height={containerSx ? "100%" : undefined}
				leftAxisData={
					transformedDataLeft
						? {
								stackedAreaGroups: [transformedDataLeft],
								maxValue: stackedMax,
								tickRenderer: (value) => {
									return `${value.toFixed(0)}%`;
								},
							}
						: undefined
				}
				rightAxisData={
					transformedDataRight
						? {
								dataGroups: transformedDataRight,
								maxValue: censusDomain?.max,
								minValue: censusDomain?.min,
								tickRenderer: (value) => {
									return `${value}${value === censusDomain?.max ? "🧑" : ""}`;
								},
							}
						: undefined
				}
				tooltipSortOrder={{ "DAILY OT(%)": 0, "WEEKLY OT(%)": 1, "AGENCY(%)": 2, CENSUS: 3 }}
			/>
		</CommonDashboardBox>
	);
};
